import { useContext, useMemo, useState } from 'react';
import { CONTEXTS, CONTROLLERS } from 'figshelf-modules';
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Alert,
    InputAdornment,
    IconButton,
    CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, LockOutlined } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const Login = () => {
    // Access the context directly
    const navigate = useNavigate();
    const location = useLocation();
    const auth = useContext(CONTEXTS.AuthContext);
    const api = useContext(CONTEXTS.APIContext);
    const authController = new CONTROLLERS.AuthController(api);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const trimmedIdentifier = useMemo(() => String(email || '').trim(), [email]);
    const trimmedPassword = useMemo(() => String(password || ''), [password]);

    const identifierError = useMemo(() => {
        if (!trimmedIdentifier) return 'Email or username is required';
        if (trimmedIdentifier.length < 3) return 'Enter at least 3 characters';
        return '';
    }, [trimmedIdentifier]);

    const passwordError = useMemo(() => {
        if (!trimmedPassword) return 'Password is required';
        if (trimmedPassword.length < 8) return 'Password must be at least 8 characters';
        return '';
    }, [trimmedPassword]);

    const handleLogin = async () => {
        if (identifierError || passwordError) {
            setError(identifierError || passwordError);
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const res = await authController.loginAdmin(trimmedIdentifier, trimmedPassword);
            const success = await auth.signIn(res.data.accessToken, res.data.refreshToken);
            
            if (success) {
                // Redirect to the page they were trying to access, or home
                const from = location.state?.from?.pathname || '/';
                navigate(from, { replace: true });
            } else {
                setError('Login failed. Please try again.');
            }
        } catch (err) {
            console.error('Login error:', err);
            const apiMessage = err?.response?.data?.message;
            setError(apiMessage || err.message || 'Login failed. Please check your credentials.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleLogin();
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'radial-gradient(1200px circle at 20% 10%, rgba(25,118,210,0.18), transparent 40%),\nradial-gradient(900px circle at 80% 30%, rgba(156,39,176,0.14), transparent 45%),\nlinear-gradient(180deg, #f4f6f8 0%, #eef2f6 100%)',
                px: 2,
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 3, sm: 4 },
                    maxWidth: 440,
                    width: '100%',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.10)',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'linear-gradient(135deg, rgba(25,118,210,0.15), rgba(156,39,176,0.12))',
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <LockOutlined color="action" />
                    </Box>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                            Admin Console
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Sign in to continue
                        </Typography>
                    </Box>
                </Box>
                
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box
                    component="form"
                    sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1.75 }}
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleLogin();
                    }}
                >
                    <TextField
                        label="Email or Username"
                        variant="outlined"
                        fullWidth
                        autoComplete="username"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyPress={handleKeyPress}
                        disabled={isLoading}
                        error={Boolean(identifierError) && Boolean(trimmedIdentifier)}
                        helperText={Boolean(identifierError) && Boolean(trimmedIdentifier) ? identifierError : ' '}
                    />
                    <TextField
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        variant="outlined"
                        fullWidth
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyPress={handleKeyPress}
                        disabled={isLoading}
                        error={Boolean(passwordError) && Boolean(trimmedPassword)}
                        helperText={Boolean(passwordError) && Boolean(trimmedPassword) ? passwordError : ' '}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                        onClick={() => setShowPassword((v) => !v)}
                                        edge="end"
                                        disabled={isLoading}
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        fullWidth
                        sx={{ mt: 0.5, height: 44, borderRadius: 2, fontWeight: 800 }}
                        type="submit"
                        disabled={isLoading || Boolean(identifierError) || Boolean(passwordError)}
                    >
                        {isLoading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={18} color="inherit" />
                                <span>Signing in…</span>
                            </Box>
                        ) : (
                            'Sign in'
                        )}
                    </Button>
                </Box>
            </Paper>
        </Box>
    )
}

export default Login;